/// <amd-module name="Artifacts/Pypi/services/PyPiDataService" />
// import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { BatchExecutor, IBatchIterator } from "Artifacts/Package/services/BatchHandling";
import * as VSS_PyPiApi_Contracts from "Artifacts/Pypi/Contracts/PyPiApi";
import { getPyPiApiClient, IPyPiApiRestClient } from "Artifacts/Pypi/RestClient/PyPiApi";
import { MinimalPackageDetails, UpstreamingBehavior } from "Artifacts/Root/types/PackagingShared";
import { IVssService, Services, VssService } from "VSS/Platform/Context";

export interface IFeedPyPiDataService extends IVssService {
  readonly PyPiApiRestClient: IPyPiApiRestClient;
  batchSize: number;
  batchDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void>;
  batchRestoreToFeed(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void>;
  batchPermanentlyDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void>;
  batchPromote(
    projectId: string,
    feedId: string,
    viewId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void>;
  getUpstreamingBehavior(
    feedId: string,
    packageName: string,
    projectId?: string,
  ): Promise<UpstreamingBehavior>;
  setUpstreamingBehavior(
    feedId: string,
    packageName: string,
    behavior: UpstreamingBehavior,
    projectId?: string,
  ): Promise<void>;
}

class PyPiDataService extends VssService implements IFeedPyPiDataService {
  get PyPiApiRestClient(): IPyPiApiRestClient {
    return getPyPiApiClient(this.pageContext);
  }

  get batchSize() {
    return 100; // SGV TODO
    // return this.pageContext
    // 	.getService<HubWebPageDataService>("IFeedWebPageDataService")
    // 	.getBatchOperationPageSize();
  }

  public async batchDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <VSS_PyPiApi_Contracts.PyPiPackagesBatchRequest>{
          data: null,
          operation: VSS_PyPiApi_Contracts.PyPiBatchOperationType.Delete,
          packages: packageDetails,
        };
        await this.PyPiApiRestClient.updatePackageVersions(batchRequest, feedId, projectId);
      },
    );
  }

  public async batchRestoreToFeed(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <VSS_PyPiApi_Contracts.PyPiPackagesBatchRequest>{
          data: null,
          operation: VSS_PyPiApi_Contracts.PyPiBatchOperationType.RestoreToFeed,
          packages: packageDetails,
        };
        await this.PyPiApiRestClient.updateRecycleBinPackageVersions(
          batchRequest,
          feedId,
          projectId,
        );
      },
    );
  }

  public async batchPermanentlyDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <VSS_PyPiApi_Contracts.PyPiPackagesBatchRequest>{
          data: null,
          operation: VSS_PyPiApi_Contracts.PyPiBatchOperationType.PermanentDelete,
          packages: packageDetails,
        };
        await this.PyPiApiRestClient.updateRecycleBinPackageVersions(
          batchRequest,
          feedId,
          projectId,
        );
      },
    );
  }

  public async batchPromote(
    projectId: string,
    feedId: string,
    viewId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <VSS_PyPiApi_Contracts.PyPiPackagesBatchRequest>{
          data: { viewId },
          operation: VSS_PyPiApi_Contracts.PyPiBatchOperationType.Promote,
          packages: packageDetails,
        };
        await this.PyPiApiRestClient.updatePackageVersions(batchRequest, feedId, projectId);
      },
    );
  }

  public getUpstreamingBehavior(
    feedId: string,
    packageName: string,
    projectId?: string,
  ): Promise<UpstreamingBehavior> {
    return this.PyPiApiRestClient.getUpstreamingBehavior(feedId, packageName, projectId);
  }

  public setUpstreamingBehavior(
    feedId: string,
    packageName: string,
    behavior: UpstreamingBehavior,
    projectId: string,
  ): Promise<void> {
    return this.PyPiApiRestClient.setUpstreamingBehavior(feedId, packageName, behavior, projectId);
  }
}

Services.add("IFeedPyPiDataService", { serviceFactory: PyPiDataService });
